// src/features/workflow/DebugPanel.tsx
// Minimal debug surface for the pipeline editor. It does NOT rebuild the canvas
// — it is a side panel that starts/stops a debug session, manages breakpoints
// (optionally on the node currently selected in the canvas), and steps
// execution. The canvas passes in the selected node id so "break here" is one
// click.

import { useState, useCallback } from 'react';
import { Button, Badge } from '@react/components';
import { getEntityId, toBadgeVariant } from './logic';

interface DebugPanelProps {
  pipelineId: string;
  selectedNodeId: string | null;
  store: any;
  t: (k: string) => string;
  onClose: () => void;
}

export function DebugPanel({ pipelineId, selectedNodeId, store, t, onClose }: DebugPanelProps) {
  const [session, setSession] = useState<any>(null);
  const [busy, setBusy] = useState(false);

  const sessionId = session ? getEntityId(session) : null;
  const active = !!session?.is_active;
  const breakpoints: any[] = session?.breakpoints || [];

  const start = useCallback(async () => {
    setBusy(true);
    try {
      const res = await store.StartDebugSession?.(pipelineId, { breakpoints: [] });
      setSession(res?.session ?? res);
    } finally {
      setBusy(false);
    }
  }, [pipelineId]);

  const stop = useCallback(async () => {
    if (!sessionId) return;
    setBusy(true);
    try {
      await store.StopDebugSession?.(sessionId, {});
      setSession(null);
    } finally {
      setBusy(false);
    }
  }, [sessionId]);

  const step = useCallback(async (mode: string) => {
    if (!sessionId) return;
    setBusy(true);
    try {
      const res = await store.DebugStep?.(sessionId, { mode: mode as any });
      setSession(res?.session ?? res);
    } finally {
      setBusy(false);
    }
  }, [sessionId]);

  const addBreakpoint = useCallback(async () => {
    if (!sessionId || !selectedNodeId) return;
    setBusy(true);
    try {
      const res = await store.SetBreakpoint?.(sessionId, { node_id: selectedNodeId });
      setSession(res?.session ?? res);
    } finally {
      setBusy(false);
    }
  }, [sessionId, selectedNodeId]);

  const removeBreakpoint = useCallback(async (bpId: string) => {
    if (!sessionId) return;
    setBusy(true);
    try {
      const res = await store.RemoveBreakpoint?.(sessionId, bpId);
      setSession(res?.session ?? res);
    } finally {
      setBusy(false);
    }
  }, [sessionId]);

  return (
    <div className="w-72 border-l border-border bg-background overflow-y-auto flex-shrink-0 flex flex-col">
      <div className="flex items-center justify-between border-b border-border p-3">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold text-foreground">{t('workflow.debug.title')}</h3>
          <Badge variant={toBadgeVariant(active ? 'success' : 'secondary')} size="sm">
            {active ? t('workflow.debug.active') : t('workflow.debug.inactive')}
          </Badge>
        </div>
        <button type="button" onClick={onClose} aria-label={t('workflow.hidePanel')} className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground">
          <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M18 6L6 18M6 6l12 12" /></svg>
        </button>
      </div>

      <div className="p-3 space-y-4">
        {!session ? (
          <Button size="sm" className="w-full" onClick={start} isLoading={busy}>{t('workflow.debug.start')}</Button>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-2">
              <Button size="sm" variant="secondary" disabled={busy} onClick={() => step('DEBUG_MODE_STEP_OVER')}>{t('workflow.debug.step')}</Button>
              <Button size="sm" variant="secondary" disabled={busy} onClick={() => step('DEBUG_MODE_STEP_INTO')}>{t('workflow.debug.stepInto')}</Button>
              <Button size="sm" variant="secondary" disabled={busy} onClick={() => step('DEBUG_MODE_CONTINUE')}>{t('workflow.debug.continue')}</Button>
              <Button size="sm" variant="ghost" disabled={busy} onClick={stop}>{t('workflow.debug.stop')}</Button>
            </div>

            {session.current_node_id && (
              <div className="text-xs">
                <span className="text-muted-foreground">{t('workflow.debug.currentNode')}: </span>
                <span className="font-mono text-foreground">{session.current_node_id}</span>
              </div>
            )}

            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-medium text-foreground">{t('workflow.debug.breakpoints')}</p>
                <Button size="sm" variant="ghost" disabled={busy || !selectedNodeId} onClick={addBreakpoint} title={selectedNodeId || t('workflow.debug.selectNode')}>
                  {t('workflow.debug.addBreakpoint')}
                </Button>
              </div>
              {breakpoints.length === 0 ? (
                <p className="text-xs text-muted-foreground">{t('workflow.debug.noBreakpoints')}</p>
              ) : (
                <ul className="space-y-1">
                  {breakpoints.map((bp) => (
                    <li key={bp.id} className="flex items-center justify-between text-xs bg-muted rounded px-2 py-1">
                      <span className="font-mono text-foreground truncate">{bp.node_id}</span>
                      <button type="button" onClick={() => removeBreakpoint(bp.id)} aria-label={t('common.delete')} className="text-muted-foreground hover:text-destructive ml-2">
                        <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M18 6L6 18M6 6l12 12" /></svg>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default DebugPanel;
